import type { Drill } from "./bank/types.ts";
import type { Progress } from "./api.ts";

export type Queued = {
  drill: Drill;
  /** Why this one came up, so the UI can be honest about it rather than feeling random. */
  reason: "due" | "new" | "ahead";
  progress?: Progress;
};

/**
 * Decide what to serve next.
 *
 * The order is: what you owe, then what you have never seen, then everything
 * else pulled forward. That last case is what makes a long session possible
 * without wrecking the schedule: extra reps are marked `ahead`, and the server
 * grows their interval by half as much, because recalling something you saw an
 * hour ago proves much less than recalling it a week later.
 *
 * `avoid` is every drill already served in this sitting, not just the last one.
 * With only the last one excluded, skipping twice ping-pongs between the first
 * two candidates forever, which makes Skip useless for its actual purpose of
 * getting away from something you have seen too recently. When everything has
 * been excluded the list is ignored rather than returning nothing: running out
 * of unseen drills should hand you a repeat, not an empty screen.
 */
export function pickNext(
  bank: Drill[],
  progress: Map<string, Progress>,
  now: Date,
  avoid: string[] = [],
): Queued | null {
  if (bank.length === 0) return null;

  const candidates: Queued[] = bank.map((drill) => {
    const p = progress.get(drill.id);
    if (!p) return { drill, reason: "new" as const };
    return {
      drill,
      reason: new Date(p.dueAt) <= now ? ("due" as const) : ("ahead" as const),
      progress: p,
    };
  });

  const rank = { due: 0, new: 1, ahead: 2 };
  const sorted = candidates.sort((a, b) => {
    if (rank[a.reason] !== rank[b.reason]) return rank[a.reason] - rank[b.reason];
    // Within due, the most overdue first. Within ahead, the soonest due first.
    // Both are "closest to the edge of forgetting", which is where a rep is worth most.
    const aDue = a.progress ? new Date(a.progress.dueAt).getTime() : 0;
    const bDue = b.progress ? new Date(b.progress.dueAt).getTime() : 0;
    return aDue - bDue;
  });

  const excluded = new Set(avoid);
  const preferred = sorted.find((c) => !excluded.has(c.drill.id));
  return preferred ?? sorted[0] ?? null;
}

/**
 * Queue one specific drill, chosen by hand rather than by the scheduler.
 *
 * The reason is still derived from the schedule rather than being a fourth kind
 * of its own, because the reason is what the header shows and what the server
 * has already decided. Picking a drill you do not owe yet is an extra rep and is
 * labelled as one: the server sees the stored due date, marks the attempt ahead,
 * and grows the interval by half. Choosing for yourself therefore cannot inflate
 * a schedule, which is what made it safe to allow at all.
 */
export function queueFor(drill: Drill, progress: Map<string, Progress>, now: Date): Queued {
  const p = progress.get(drill.id);
  if (!p) return { drill, reason: "new" };
  return { drill, reason: new Date(p.dueAt) <= now ? "due" : "ahead", progress: p };
}

/**
 * The drills a user is enrolled to see.
 *
 * Applied before every other scheduling decision, so a subject you never signed
 * up for can never be due, counted, or served. An unknown or empty enrolment
 * falls back to the whole bank rather than to nothing: showing the wrong drill
 * is recoverable, showing none looks broken.
 */
export function inTracks(bank: Drill[], tracks: string[] | undefined): Drill[] {
  if (!tracks || tracks.length === 0) return bank;
  const enrolled = new Set(tracks);
  const filtered = bank.filter((d) => enrolled.has(d.track));
  return filtered.length > 0 ? filtered : bank;
}

/** How many drills are owed right now. Drives the "due" badge and the end-of-queue message. */
export function dueCount(bank: Drill[], progress: Map<string, Progress>, now: Date): number {
  return bank.filter((d) => {
    const p = progress.get(d.id);
    return p != null && new Date(p.dueAt) <= now;
  }).length;
}

/** Drills that have never been attempted. New material is only introduced once nothing is owed. */
export function newCount(bank: Drill[], progress: Map<string, Progress>): number {
  return bank.filter((d) => !progress.has(d.id)).length;
}

export function formatDue(dueAt: string, now: Date): string {
  const ms = new Date(dueAt).getTime() - now.getTime();
  if (ms <= 0) return "due now";
  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return `in ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 36) return `in ${hours}h`;
  return `in ${Math.round(hours / 24)}d`;
}

export function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}
